/**
 * Featured-reviews — scroll-snap kaarten-slider.
 *
 * Geen slider-library en geen transform-slider: de kaarten-track is een native
 * horizontaal scrollbaar scroll-snap-gebied. Dit script bedraadt alleen de twee
 * pijlknoppen (scrollBy per kaartbreedte), houdt hun disabled-staat bij op de
 * scrollpositie en verbergt de controls als alles past (geen overflow). Native
 * swipe/trackpad en toetsenbord-scroll van de track werken zonder JS.
 *
 * Import in resources/js/main.js; self-init op DOM-ready. No-op zonder blokken.
 */

const BLOCK = '[data-featured-reviews]';
const TRACK = '[data-featured-reviews-track]';
const CONTROLS = '[data-featured-reviews-controls]';
const PREV = '[data-featured-reviews-prev]';
const NEXT = '[data-featured-reviews-next]';
const READMORE = '[data-fr-readmore]';
const EXPANDED = 'featured-reviews__card--expanded';

// Marge zodat sub-pixel-afrondingen aan het eind niet blijven hangen op "kan nog".
const EDGE = 2;

/**
 * Bedraadt de Lees-meer-knoppen: toont een knop alleen bij ECHTE overflow van de
 * quote (scrollHeight > clientHeight, per breakpoint gemeten), en togglet de
 * uitgeklapte staat (clamp weg) + aria-expanded + het label. De volledige tekst
 * blijft altijd in de DOM.
 *
 * @param {HTMLElement} block    - De blok-root ([data-featured-reviews]).
 * @param {Function}    onToggle - Callback ná het uitklappen (slider hersyncen).
 *
 * @returns {Function} Her-evalueer-functie (aan te roepen bij resize).
 */
function initReadMore(block, onToggle) {
    const pairs = [];
    block.querySelectorAll(READMORE).forEach((btn) => {
        const id = btn.getAttribute('aria-controls');
        const quote = id ? document.getElementById(id) : null;
        const card = quote ? quote.closest('.featured-reviews__card') : null;
        if (quote && card) {
            pairs.push({ btn, quote, card });
        }
    });

    // Her-evalueer de overflow; alleen op INgeklapte kaarten (uitgeklapt = clamp weg,
    // dus scrollHeight == clientHeight → dan de knop niet wegnemen).
    const evaluate = () => {
        pairs.forEach(({ btn, quote, card }) => {
            if (card.classList.contains(EXPANDED)) {
                return;
            }
            btn.hidden = quote.scrollHeight - quote.clientHeight <= EDGE;
        });
    };

    pairs.forEach(({ btn, card }) => {
        btn.addEventListener('click', () => {
            const expanded = card.classList.toggle(EXPANDED);
            btn.setAttribute('aria-expanded', String(expanded));
            btn.textContent = expanded
                ? btn.dataset.frLess || 'Lees minder'
                : btn.dataset.frMore || 'Lees meer';
            // Kaarthoogte veranderde → de slider-staat herberekenen.
            if (typeof onToggle === 'function') {
                onToggle();
            }
        });
    });

    evaluate();
    return evaluate;
}

/**
 * Leest de horizontale gap (px) van de track uit de computed style.
 *
 * @param {HTMLElement} track - Het scroll-track-element.
 *
 * @returns {number} De kolom-gap in pixels (0 als niet leesbaar).
 */
function trackGap(track) {
    const gap = parseFloat(getComputedStyle(track).columnGap);
    return Number.isFinite(gap) ? gap : 0;
}

/**
 * Bepaalt de schuif-afstand: één kaartbreedte + de gap (schuiven per kaart).
 *
 * @param {HTMLElement} track - Het scroll-track-element.
 *
 * @returns {number} De stap in pixels (fallback: 80% van de zichtbare breedte).
 */
function stepSize(track) {
    const card = track.querySelector('.featured-reviews__card');
    if (card) {
        return card.getBoundingClientRect().width + trackGap(track);
    }
    return track.clientWidth * 0.8;
}

/**
 * Initialiseert één featured-reviews-slider: overflow-detectie, pijl-staat en
 * de klik-handlers.
 *
 * @param {HTMLElement} block - De blok-root ([data-featured-reviews]).
 */
function initSlider(block) {
    const track = block.querySelector(TRACK);
    const controls = block.querySelector(CONTROLS);
    const prev = block.querySelector(PREV);
    const next = block.querySelector(NEXT);
    if (!track || !controls || !prev || !next) {
        return;
    }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Toon de controls alleen bij echte overflow; werk de disabled-staat bij naar
    // de scrollpositie (begin → prev uit; eind → next uit).
    const sync = () => {
        const overflow = track.scrollWidth - track.clientWidth > EDGE;
        controls.hidden = !overflow;
        if (!overflow) {
            return;
        }
        prev.disabled = track.scrollLeft <= EDGE;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - EDGE;
    };

    const scrollByStep = (direction) => {
        track.scrollBy({
            left: direction * stepSize(track),
            behavior: reduce.matches ? 'auto' : 'smooth',
        });
    };

    prev.addEventListener('click', () => scrollByStep(-1));
    next.addEventListener('click', () => scrollByStep(1));

    // Lees-meer-knoppen; na een toggle de slider-staat opnieuw berekenen.
    const reEvaluate = initReadMore(block, sync);

    // Scroll-staat throttlen via rAF zodat we niet op elke scroll-tick herrekenen.
    let frame = 0;
    track.addEventListener('scroll', () => {
        if (frame) {
            return;
        }
        frame = window.requestAnimationFrame(() => {
            frame = 0;
            sync();
        });
    });

    // Bij resize verandert de kaartbreedte → zowel de slider-overflow als de
    // quote-overflow (een quote die op desktop past kan op mobiel overlopen).
    const onResize = () => {
        sync();
        reEvaluate();
    };
    if (typeof ResizeObserver === 'function') {
        new ResizeObserver(onResize).observe(track);
    } else {
        window.addEventListener('resize', onResize);
    }

    sync();
}

/**
 * Initialiseert alle featured-reviews-sliders binnen een scope.
 *
 * @param {ParentNode} [scope] - Zoekwortel (default: document).
 */
export function initFeaturedReviews(scope = document) {
    scope.querySelectorAll(BLOCK).forEach(initSlider);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFeaturedReviews());
} else {
    initFeaturedReviews();
}
